import { useRef, useState } from 'react'

export type ErrorCheckTriggerType = 'onBlur' | 'onSubmit' | 'onChange'

export interface InputModel {
    value?: string
    errorChecks?: ErrorCheck[]
    errorCheckDebounceMs?: number
}

export interface ErrorCheck {
    trigger: ErrorCheckTriggerType[]
    condition: (value: string, formValues: { [key: string]: string }) => boolean
    message: ((value: string) => string) | string
}

export interface InputBindType {
    [key: string]: {
        value: string
        error: boolean
        helperText: string
        onChange: any
        onBlur: any
    }
}

export default function useInput(initialValue: { [key: string]: InputModel }) {
    const [formModelData, setFormModelData] = useState(initialValue)
    const [errormap, setErrormap] = useState<{ [key: string]: string | null }>({})

    const debounceMap = useRef<{ [key: string]: NodeJS.Timer | null }>({})

    const initialFormValues: { [key: string]: string } = {}
    Object.keys(initialValue).forEach((key) => {
        initialFormValues[key] = initialValue[key].value || ''
    })

    const [formValues, setFormValues] = useState(initialFormValues)
    const errorValueMap = useRef<{ [key: string]: string | null }>({})

    const checkWrapper = (key: string, value: string, trigger: ErrorCheckTriggerType) => {
        if (formModelData[key].errorCheckDebounceMs) {
            if (debounceMap.current[key]) {
                // @ts-ignore
                clearTimeout(debounceMap.current[key])
            }
            debounceMap.current[key] = setTimeout(() => {
                check(key, value, trigger)
            }, formModelData[key].errorCheckDebounceMs)
        } else {
            check(key, value, trigger)
        }
    }

    const bind: InputBindType = {}
    Object.keys(initialValue).forEach((key: string) => {
        bind[key] = {
            value: formValues[key] || '',
            error: errormap[key] != null,
            helperText: errormap[key] || ' ',
            onChange: (event: any) => {
                const value: string = typeof event === 'string' ? event : event.target.value
                setFormValues((values) => ({
                    ...values,
                    [key]: value,
                }))
                setErrormap({
                    ...errormap,
                    [key]: null,
                })
                checkWrapper(key, value, 'onChange')
            },
            onBlur: (event: any) => {
                checkWrapper(key, formValues[key], 'onBlur')
            },
        }
    })

    const setError = (key: string, value: string) => {
        setErrormap({
            ...errormap,
            [key]: value,
        })
        errorValueMap.current = {
            ...errorValueMap.current,
            [key]: formValues[key],
        }
    }

    const check = async (key: string, value: string, trigger: ErrorCheckTriggerType): Promise<string | null> => {
        const setError = (message: string | null) => {
            setErrormap({ ...errormap, [key]: message })
            errorValueMap.current = {
                ...errorValueMap.current,
                [key]: value,
            }
        }

        return new Promise(async (resolve, _) => {
            if (!formModelData[key] || formModelData[key].errorChecks == null) {
                setError(null)
                return resolve(null)
            }

            // @ts-ignore
            for (let errorCheck of formModelData[key].errorChecks) {
                if (errorCheck.trigger.includes(trigger)) {
                    let showError: boolean | string = await errorCheck.condition(value, formValues)
                    if (showError) {
                        if (typeof showError === 'string') {
                            setError(showError)
                            return resolve(showError)
                        } else if (typeof errorCheck.message === 'function') {
                            let message = errorCheck.message(value)
                            setError(message)
                            return resolve(message)
                        } else {
                            setError(errorCheck.message)
                            return resolve(errorCheck.message)
                        }
                    }
                }
            }

            if (value && errorValueMap.current[key] === value) {
                // Keep previous error if the value didn't change.
                return resolve(errormap[key])
            } else {
                setError(null)
                return resolve(null)
            }
        })
    }

    const submit = async () => {
        const errormap_next = { ...errormap }
        let hasErrors = false
        for (let key in formModelData) {
            let error = await check(key, formValues[key], 'onSubmit')
            if (error != null) {
                errormap_next[key] = error
                hasErrors = true
            }
        }

        setErrormap(errormap_next)

        return {
            hasErrors: hasErrors,
            formdata: formModelData,
        }
    }

    const setValue = (field: string, value: string) => {
        setFormValues((values) => ({
            ...values,
            [field]: value,
        }))
    }

    return {
        formdata: formValues,
        setValue: setValue,
        setError,
        submit,
        bind,
    }
}

export const Conditions = {
    is_epsilon: (value: string) => value.length === 0,
    is_empty: (value: string) => value.trim().length === 0,
    no_contain_spaces: (value: string) => value.trim().match(/\s/),
}
